import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { ArrowLeft, FileJson, Table, Download, Upload, Layers, FolderPlus, FileSpreadsheet, CloudDownload, AlertTriangle, Loader2, Check, Library } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  initGoogleDrive,
  hasGoogleAccess,
  restoreFromGoogleDrive,
  checkGoogleDriveBackup,
  startGoogleRedirectAuth,
  handleGoogleRedirectCallback
} from '../services/cloudBackup';

export function ImportHelp() {
  const jsonExample = `{
  "deck_name": "My Knowledge Base",
  "cards": [
    {
      "title": "Albert Einstein",
      "notes": "Famous physicist",
      "fields": {
        "Born": "March 14, 1879",
        "Nationality": "German, American",
        "Field": "Physics"
      }
    }
  ]
}`;

  const csvExample = `Title,Notes,Born,Nationality,Field
Albert Einstein,Famous physicist,March 14 1879,"German, American",Physics
Marie Curie,Pioneer in radioactivity,November 7 1867,Polish,Chemistry`;

  // Google Drive restore state
  const [googleReady, setGoogleReady] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleBackupInfo, setGoogleBackupInfo] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

  useEffect(() => {
    async function init() {
      const redirectResult = handleGoogleRedirectCallback();
      if (redirectResult?.success) {
        toast.success('Connected to Google Drive!');
      }

      const googleOk = await initGoogleDrive();
      setGoogleReady(googleOk);
      setGoogleConnected(hasGoogleAccess());

      if (hasGoogleAccess()) {
        const info = await checkGoogleDriveBackup();
        setGoogleBackupInfo(info);
      }
    }
    init();
  }, []);

  const handleRestoreFromGoogle = async () => {
    try {
      setGoogleLoading(true);
      const result = await restoreFromGoogleDrive();
      toast.success(result.message);
      setShowRestoreConfirm(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.message || 'Restore failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="import-help">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl font-bold">Import</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">

        {/* Restore from Google Drive */}
        {googleReady && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <CloudDownload className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Restore from Google Drive</CardTitle>
                    <CardDescription>Replace all current data with your saved backup</CardDescription>
                  </div>
                </div>
                {googleConnected && (
                  <Badge variant="outline" className="gap-1">
                    <Check className="h-3 w-3" /> Connected
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {!googleConnected ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Connect Google Drive to restore from a previous backup.
                  </p>
                  <Button variant="outline" onClick={() => startGoogleRedirectAuth()}>
                    <CloudDownload className="h-4 w-4 mr-2" />
                    Connect Google Drive
                  </Button>
                </div>
              ) : !googleBackupInfo?.exists ? (
                <p className="text-sm text-muted-foreground">
                  No backup found in your Google Drive yet. Export one from{' '}
                  <Link to="/backup" className="underline hover:text-foreground">Backup & Storage</Link>.
                </p>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Backup saved: {formatDate(googleBackupInfo.lastModified)}
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      <strong>Warning:</strong> Restoring will replace all current data on this device. This cannot be undone.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowRestoreConfirm(true)}
                    disabled={googleLoading}
                  >
                    {googleLoading
                      ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <CloudDownload className="h-4 w-4 mr-2" />}
                    Restore from Google Drive
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Supported File Types */}
        <Card className="mb-8 border-primary/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Supported Import Formats
            </CardTitle>
            <CardDescription>
              MindDeck accepts spreadsheets (.xlsx, .xls), CSV, and JSON files
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <FileSpreadsheet className="h-5 w-5 text-green-600" />
                  <span className="font-semibold">.xlsx / .xls</span>
                </div>
                <p className="text-sm text-muted-foreground">Excel spreadsheets - easiest to create</p>
              </div>
              <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-2">
                  <Table className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold">.csv</span>
                </div>
                <p className="text-sm text-muted-foreground">Comma-separated values - universal format</p>
              </div>
              <div className="border rounded-lg p-4 bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2 mb-2">
                  <FileJson className="h-5 w-5 text-purple-600" />
                  <span className="font-semibold">.json</span>
                </div>
                <p className="text-sm text-muted-foreground">Structured data - best for backup/restore</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Spreadsheet Format */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Spreadsheet Format (.xlsx, .xls)
            </CardTitle>
            <CardDescription>
              Two layouts supported - auto-detected on import
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Horizontal Format */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-0.5 rounded text-xs">HORIZONTAL</span>
                Each Row = One Card
              </h4>
              <p className="text-sm text-muted-foreground mb-3">
                Row 1 contains headers. Each subsequent row is a card. Best for importing many cards at once.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full border text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="border px-3 py-2 text-left"></th>
                      <th className="border px-3 py-2 text-left font-mono">A</th>
                      <th className="border px-3 py-2 text-left font-mono">B</th>
                      <th className="border px-3 py-2 text-left font-mono">C</th>
                      <th className="border px-3 py-2 text-left font-mono">D</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-primary/5">
                      <td className="border px-3 py-2 font-mono text-muted-foreground">1</td>
                      <td className="border px-3 py-2 font-semibold">Title</td>
                      <td className="border px-3 py-2 font-semibold">Notes</td>
                      <td className="border px-3 py-2 font-semibold">Borough</td>
                      <td className="border px-3 py-2 font-semibold">Population</td>
                    </tr>
                    <tr>
                      <td className="border px-3 py-2 font-mono text-muted-foreground">2</td>
                      <td className="border px-3 py-2">NYC</td>
                      <td className="border px-3 py-2">Big Apple</td>
                      <td className="border px-3 py-2">Manhattan, Brooklyn</td>
                      <td className="border px-3 py-2">8.3M</td>
                    </tr>
                    <tr>
                      <td className="border px-3 py-2 font-mono text-muted-foreground">3</td>
                      <td className="border px-3 py-2">LA</td>
                      <td className="border px-3 py-2">City of Angels</td>
                      <td className="border px-3 py-2">Hollywood</td>
                      <td className="border px-3 py-2">4M</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                ↑ Creates 2 cards: "NYC" and "LA" with their respective fields
              </p>
            </div>

            {/* Vertical Format */}
            <div className="pt-4 border-t">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded text-xs">VERTICAL</span>
                Column A = Labels, Column B+ = Card Data
              </h4>
              <p className="text-sm text-muted-foreground mb-3">
                Column A contains field labels. Each additional column is a card. Best for cards with many fields.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full border text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="border px-3 py-2 text-left"></th>
                      <th className="border px-3 py-2 text-left font-mono bg-primary/10">A (Labels)</th>
                      <th className="border px-3 py-2 text-left font-mono">B</th>
                      <th className="border px-3 py-2 text-left font-mono">C</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border px-3 py-2 font-mono text-muted-foreground">1</td>
                      <td className="border px-3 py-2 font-semibold bg-primary/5">Title</td>
                      <td className="border px-3 py-2">NYC</td>
                      <td className="border px-3 py-2">LA</td>
                    </tr>
                    <tr>
                      <td className="border px-3 py-2 font-mono text-muted-foreground">2</td>
                      <td className="border px-3 py-2 font-semibold bg-primary/5">Notes</td>
                      <td className="border px-3 py-2">Big Apple</td>
                      <td className="border px-3 py-2">City of Angels</td>
                    </tr>
                    <tr>
                      <td className="border px-3 py-2 font-mono text-muted-foreground">3</td>
                      <td className="border px-3 py-2 font-semibold bg-primary/5">Borough</td>
                      <td className="border px-3 py-2">Manhattan, Brooklyn</td>
                      <td className="border px-3 py-2">Hollywood</td>
                    </tr>
                    <tr>
                      <td className="border px-3 py-2 font-mono text-muted-foreground">4</td>
                      <td className="border px-3 py-2 font-semibold bg-primary/5">Population</td>
                      <td className="border px-3 py-2">8.3M</td>
                      <td className="border px-3 py-2">4M</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                ↑ Creates 2 cards: "NYC" (column B) and "LA" (column C) with their respective fields
              </p>
            </div>

            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Column Structure</h4>
              <div className="text-sm text-amber-700 dark:text-amber-300 space-y-2">
                <p><strong>Headers:</strong> <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">Title</code>, <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">Notes</code>, <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">[Field1]</code>, <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">[Field2]</code>, ...</p>
                <p><strong>Data:</strong> <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">[Card Name]</code>, <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">[Notes]</code>, <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">[Value1]</code>, <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">[Value2]</code>, ...</p>
                <p className="text-xs mt-2">Only <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">Title</code> and <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">Notes</code> are reserved. All other columns become field:value pairs.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CSV Format */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Table className="h-5 w-5 text-blue-600" />
              CSV Format (.csv)
            </CardTitle>
            <CardDescription>
              Horizontal format only - same as spreadsheet horizontal layout
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              First row = headers, subsequent rows = card data. Use quotes for values containing commas.
            </p>
            <div className="bg-muted rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm font-mono">{csvExample}</pre>
            </div>
            <p className="text-xs text-muted-foreground">
              ↑ Creates 2 cards: "Albert Einstein" and "Marie Curie"
            </p>
          </CardContent>
        </Card>

        {/* JSON Format */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileJson className="h-5 w-5 text-purple-600" />
              JSON Format (.json)
            </CardTitle>
            <CardDescription>
              Most flexible - supports all card properties
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Full control over card structure. Recommended for backup/restore and sharing decks.
            </p>
            <div className="bg-muted rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm font-mono">{jsonExample}</pre>
            </div>
            <div className="text-sm space-y-1">
              <p><code className="bg-muted px-1 rounded">deck_name</code> - Name for the imported deck (optional)</p>
              <p><code className="bg-muted px-1 rounded">cards</code> - Array of card objects</p>
              <p><code className="bg-muted px-1 rounded">title</code> - Card title (required)</p>
              <p><code className="bg-muted px-1 rounded">notes</code> - Card notes (optional)</p>
              <p><code className="bg-muted px-1 rounded">fields</code> - Object of field:value pairs (optional)</p>
            </div>
          </CardContent>
        </Card>

        {/* How Import Works */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>How Import Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <ol className="list-decimal list-inside space-y-2 text-sm text-blue-700 dark:text-blue-300">
                <li>Upload your file or paste JSON</li>
                <li>System shows a <strong>preview</strong>:
                  <ul className="list-disc list-inside ml-4 mt-1">
                    <li><strong>NEW</strong> - Card will be created (title doesn't exist)</li>
                    <li><strong>MERGE</strong> - Fields added to existing card (title matches)</li>
                  </ul>
                </li>
                <li>Confirm the import</li>
                <li>A new Deck is created with all imported cards</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Architecture Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              MindDeck Structure
            </CardTitle>
            <CardDescription>
              Understanding Workspaces, Cards, and Decks
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <pre className="text-sm">
{`Workspace (isolated container)
├── Cards (with fields)
│   ├── Card "Albert Einstein"
│   │   └── Fields: Born, Nationality, Field
│   └── Card "Physics"
│       └── Fields: Type, Notable Figure
└── Decks (closed subgraphs)
    └── Deck "Scientists"
        └── Contains: Albert Einstein, Physics (auto-linked)`}
              </pre>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2">Workspaces</h4>
                <p className="text-sm text-muted-foreground">
                  Isolated containers for your knowledge. Cards in one workspace cannot reference cards in another.
                </p>
              </div>
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2">Decks</h4>
                <p className="text-sm text-muted-foreground">
                  Closed subgraphs of cards. When you add cards to a deck, all related cards are automatically included to maintain graph integrity.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Exporting Data
            </CardTitle>
            <CardDescription>
              Export a Deck as JSON for backup or sharing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Export is per-Deck. Select a deck from the filter dropdown, then click the export button.
            </p>
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Export Tips</h4>
              <ul className="list-disc list-inside text-sm text-amber-700 dark:text-amber-300 space-y-1">
                <li>Create a Deck with all cards to export everything</li>
                <li>Exported Decks are "closed" - all related cards are included</li>
                <li>JSON export preserves all data for re-importing</li>
              </ul>
            </div>
            <Link to="/backup">
              <Button variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                Go to Backup & Storage
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Bidirectional Linking */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5" />
              Bidirectional Knowledge Graph
            </CardTitle>
            <CardDescription>
              How fields automatically create linked cards
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              When you create a card with fields, MindDeck automatically creates a bidirectional knowledge graph:
            </p>
            
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="font-semibold mb-2">Example: Creating "Albert Einstein" with field "Born: March 14, 1879"</p>
              <p className="text-sm text-muted-foreground mb-3">This automatically creates:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>Card "Born" with field "March 14, 1879: Albert Einstein"</li>
                <li>Card "March 14, 1879" with field "Born: Albert Einstein"</li>
              </ul>
            </div>
            
            <p className="text-sm text-muted-foreground">
              This means you can navigate your knowledge from any angle - by person, by date, or by attribute type.
            </p>
          </CardContent>
        </Card>

        {/* Quick Start */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Quick Start</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-3">
              <li><strong>Create cards</strong> - Add cards with fields (field:value pairs)</li>
              <li><strong>Organize into decks</strong> - Group related cards into decks for easy management</li>
              <li><strong>Export and share</strong> - Export decks as JSON to share or backup</li>
              <li><strong>Import to another workspace</strong> - Import JSON to create new decks with merge preview</li>
            </ol>
            
            <div className="mt-6 flex gap-3">
              <Link to="/">
                <Button>Start Creating Cards</Button>
              </Link>
              <Link to="/decks">
                <Button variant="outline">Manage Decks</Button>
              </Link>
            </div>
          </CardContent>
        </Card>


      </main>

      {/* Restore Confirmation */}
      <AlertDialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Restore from Google Drive?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will <strong>replace all your current data</strong> with the backup from Google Drive. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestoreFromGoogle}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Yes, Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ImportHelp;
